// Finestra.tsx — finestra modale accessibile (elemento <dialog> nativo: focus, Esc, lettori di schermo).
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Indietro } from "./Indietro.tsx";

interface Props {
  aperta: boolean;
  titolo: string;
  /** v030: un'icona dopo il titolo (per esempio il mondo nella scelta della lingua) */
  icona?: ReactNode;
  onChiudi: () => void;
  children: ReactNode;
  /** v042: a schermo intero, con la freccia «indietro» a sinistra (la fine della partita: niente da scorrere dentro) */
  schermoIntero?: boolean;
}

export function Finestra({ aperta, titolo, icona, onChiudi, children, schermoIntero = false }: Props) {
  const { t } = useTranslation();
  const rif = useRef<HTMLDialogElement>(null);
  const idTitolo = useId();

  useEffect(() => {
    const d = rif.current;
    if (!d) return;
    if (aperta && !d.open) d.showModal();
    if (!aperta && d.open) d.close();
  }, [aperta]);

  // v025: sul telefono la finestra resta tutta visibile sopra la tastiera e le barre del browser (iPhone e Android
  // non rimpiccioliscono la pagina quando esce la tastiera: la parte bassa della finestra finiva sotto e sembrava tagliata).
  useEffect(() => {
    const d = rif.current;
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!aperta || !d || !vv) return;
    const misura = () => {
      const sotto = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      d.style.setProperty("--ld-vv-alto", `${Math.round(vv.height)}px`);
      d.style.setProperty("--ld-vv-sotto", `${Math.round(sotto)}px`);
    };
    misura();
    vv.addEventListener("resize", misura);
    vv.addEventListener("scroll", misura);
    return () => {
      vv.removeEventListener("resize", misura);
      vv.removeEventListener("scroll", misura);
    };
  }, [aperta]);

  return (
    <dialog
      ref={rif}
      className={schermoIntero ? "finestra finestra-schermo-intero" : "finestra"}
      aria-labelledby={idTitolo}
      onClose={onChiudi}
      onClick={(e) => {
        if (e.target === rif.current) onChiudi(); // tocco fuori dalla finestra
      }}
    >
      <div className="finestra-contenuto">
        <div className="finestra-testa">
          {schermoIntero && <Indietro onClick={onChiudi} etichetta={t("generale.chiudi")} />}
          <h2 id={idTitolo}>
            {titolo}
            {icona && <span className="finestra-icona">{icona}</span>}
          </h2>
          {!schermoIntero && (
          <button type="button" className="pulsante-icona" aria-label={t("generale.chiudi")} onClick={onChiudi}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          )}
        </div>
        {children}
      </div>
    </dialog>
  );
}
